import axios from 'axios';
import { BACKEND_BASEURL } from '@/config/config';

/**
 * Service for interacting with the XP Messenger API
 */
class XPMessengerService {

  /**
   * Test the connection to the XP Messenger API
   * @param {Object} $log - Logger instance
   * @returns {Promise<Object>} Test response from the API
   */
  async test($log) {
    try {
      // Ensure there's no double slash in the URL
      const baseUrl = BACKEND_BASEURL.endsWith('/')
        ? BACKEND_BASEURL.slice(0, -1)
        : BACKEND_BASEURL;

      $log.debug('Testing XP Messenger API connection...');

      const response = await axios.get(`${baseUrl}/messenger/test`);

      $log.debug('Test successful:', response.data);
      return response.data;
    } catch (error) {
      $log.error('Test failed:', error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        $log.error('Error response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
        throw new Error(`Test failed: ${error.response.status} ${error.response.statusText}`);
      } else if (error.request) {
        // The request was made but no response was received
        $log.error('No response received:', error.request);
        throw new Error('Test failed: No response from server');
      } else {
        // Something happened in setting up the request that triggered an Error
        $log.error('Request setup error:', error.message);
        throw new Error(`Test failed: ${error.message}`);
      }
    }
  }


  /**
   * Get all partners from the XP Messenger API
   * @param {Object} $log - Logger instance
   * @returns {Promise<Array>} List of partners
   * @throws {Error} If the request fails
   */
  async getPartners($log) {
    try {
      // Ensure there's no double slash in the URL
      const baseUrl = BACKEND_BASEURL.endsWith('/')
        ? BACKEND_BASEURL.slice(0, -1)
        : BACKEND_BASEURL;

      $log.debug(`Fetching partners`);

      const response = await axios.get(`${baseUrl}/messenger/partners`);

      $log.debug(`Successfully fetched ${response.data?.length || 0} partners`);
      return response.data;
    } catch (error) {
      $log.error('Error fetching partners:', error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        $log.error('Error response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
        throw new Error(`Failed to fetch partners: ${error.response.status} ${error.response.statusText}`);
      } else if (error.request) {
        // The request was made but no response was received
        $log.error('No response received:', error.request);
        throw new Error('Failed to fetch partners: No response from server');
      } else {
        // Something happened in setting up the request that triggered an Error
        $log.error('Request setup error:', error.message);
        throw new Error(`Failed to fetch partners: ${error.message}`);
      }
    }
  }

  /**
   * Get a specific partner by ID from the XP Messenger API
   * @param {string} partnerId - The ID of the partner to fetch
   * @param {Object} $log - Logger instance
   * @returns {Promise<Object>} Partner details
   * @throws {Error} If the request fails
   */
  async getPartner(partnerId, $log) {
    try {
      // Ensure there's no double slash in the URL
      const baseUrl = BACKEND_BASEURL.endsWith('/')
        ? BACKEND_BASEURL.slice(0, -1)
        : BACKEND_BASEURL;

      $log.debug(`Fetching partner ${partnerId}`);

      const response = await axios.get(`${baseUrl}/messenger/partners/${partnerId}`);

      $log.debug('Successfully fetched partner:', response.data);
      return response.data;
    } catch (error) {
      $log.error('Error fetching partner:', error);
      if (error.response) {
        $log.error('Error response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
        throw new Error(`Failed to fetch partner: ${error.response.status} ${error.response.statusText}`);
      } else if (error.request) {
        $log.error('No response received:', error.request);
        throw new Error('Failed to fetch partner: No response from server');
      } else {
        $log.error('Request setup error:', error.message);
        throw new Error(`Failed to fetch partner: ${error.message}`);
      }
    }
  }

  /**
   * Create a new partner
   * @param {Object} partner - The partner data to create
   * @param {Object} $log - Logger instance
   * @returns {Promise<Object>} The created partner
   * @throws {Error} If partner data is invalid or creation fails
   */
  async createPartner(partner, $log) {
    // Validate partner data
    if (!partner) {
      throw new Error('Partner data is required');
    }

    if (!partner.backendId) {
      throw new Error('Backend ID is required');
    }

    if (!partner.displayName) {
      throw new Error('Display name is required');
    }

    try {
      // Ensure there's no double slash in the URL
      const baseUrl = BACKEND_BASEURL.endsWith('/')
        ? BACKEND_BASEURL.slice(0, -1)
        : BACKEND_BASEURL;

      $log.debug(`Creating partner`);
      $log.debug('Creating partner with data:', JSON.stringify(partner, null, 2));

      const response = await axios.post(`${baseUrl}/messenger/partner`, {
        partner,
      });

      if (!response.data) {
        throw new Error('No response data received from server');
      }

      $log.debug('Successfully created partner:', response.data);
      return response.data;
    } catch (error) {
      $log.error('Error creating partner:', error);

      if (error.response) {
        $log.error('Error response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
        throw new Error(`Failed to create partner: ${error.response.status} ${error.response.statusText} - ${JSON.stringify(error.response.data)}`);
      } else if (error.request) {
        $log.error('No response received:', error.request);
        throw new Error('Failed to create partner: No response from server');
      } else {
        $log.error('Request setup error:', error.message);
        throw new Error(`Failed to create partner: ${error.message}`);
      }
    }
  }

  /**
   * Request a certificate for a partner
   * @param {string} partnerId - The ID of the partner to request certificate for
   * @param {Object} certificateData - The certificate request data
   * @param {string} certificateData.backendId - The backend ID (cn for certificate)
   * @param {string} certificateData.displayName - The display name (o for certificate)
   * @param {string} certificateData.password - The password
   * @param {Object} $log - Logger instance
   * @returns {Promise<Object>} The certificate request response
   * @throws {Error} If the request fails
   */
  async requestCertificate(partnerId, certificateData, $log) {
    // Validate partnerId
    if (!partnerId) {
      throw new Error('Partner ID is required');
    }

    // Validate certificate data
    if (!certificateData) {
      throw new Error('Certificate data is required');
    }

    if (!certificateData.backendId) {
      throw new Error('Backend ID is required');
    }

    if (!certificateData.displayName) {
      throw new Error('Display name is required');
    }

    if (!certificateData.password) {
      throw new Error('Password is required');
    }

    try {
      // Ensure there's no double slash in the URL
      const baseUrl = BACKEND_BASEURL.endsWith('/')
        ? BACKEND_BASEURL.slice(0, -1)
        : BACKEND_BASEURL;


      $log.debug(`Requesting certificate for partner ${partnerId}`);
      $log.debug('Certificate request data:', JSON.stringify(certificateData, null, 2));

      // Structure the request body according to backend specification
      const requestBody = {
        certificate: {
          backendId: certificateData.backendId,
          displayName: certificateData.displayName,
          password: certificateData.password
        }
      };

      const response = await axios.post(
        `${baseUrl}/messenger/partner/${partnerId}/certificateRequest`,
        requestBody
      );

      if (!response.data) {
        throw new Error('No response data received from server');
      }

      // Extract data from the response structure
      const result = response.data.data || response.data;

      $log.debug('Successfully requested certificate:', result);
      return result;
    } catch (error) {
      $log.error('Error requesting certificate:', error);

      if (error.response) {
        $log.error('Error response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
        throw new Error(`Failed to request certificate: ${error.response.status} ${error.response.statusText} - ${JSON.stringify(error.response.data)}`);
      } else if (error.request) {
        $log.error('No response received:', error.request);
        throw new Error('Failed to request certificate: No response from server');
      } else {
        $log.error('Request setup error:', error.message);
        throw new Error(`Failed to request certificate: ${error.message}`);
      }
    }
  }

  /**
   * Get partner registry entries from the XP Messenger API
   * @param {Object} $log - Logger instance
   * @returns {Promise<Array>} List of partner registry entries
   * @throws {Error} If the request fails
   */
  async getPartnerRegistry($log) {
    try {
      // Ensure there's no double slash in the URL
      const baseUrl = BACKEND_BASEURL.endsWith('/')
        ? BACKEND_BASEURL.slice(0, -1)
        : BACKEND_BASEURL;


      $log.debug(`Fetching partner registry entries`);

      const response = await axios.get(`${baseUrl}/messenger/registry`,);

      $log.debug(`Successfully fetched ${response.data?.length || 0} partner registry entries`);
      return response.data;
    } catch (error) {
      $log.error('Error fetching partner registry entries:', error);
      if (error.response) {
        $log.error('Error response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
        throw new Error(`Failed to fetch partner registry entries: ${error.response.status} ${error.response.statusText}`);
      } else if (error.request) {
        $log.error('No response received:', error.request);
        throw new Error('Failed to fetch partner registry entries: No response from server');
      } else {
        $log.error('Request setup error:', error.message);
        throw new Error(`Failed to fetch partner registry entries: ${error.message}`);
      }
    }
  }
}

export default new XPMessengerService();
